import React, { useState } from "react";

// Adjusted regions for an oval ground
const AREAS = [
  { name: "Third man", description: "Deep off-side behind wicket", cx: 340, cy: 80 },
  { name: "Point", description: "Square off-side", cx: 90, cy: 120 },
  { name: "Cover", description: "Off-side in front of batter", cx: 110, cy: 220 },
  { name: "Mid-off", description: "Straight off-side", cx: 170, cy: 340 },
  { name: "Long off", description: "Deep straight off-side", cx: 200, cy: 390 },
  { name: "Gully", description: "Behind point, off-side", cx: 70, cy: 160 },
  { name: "Slips", description: "Behind wicket, off-side", cx: 160, cy: 80 },
  { name: "Fine leg", description: "Deep leg-side behind wicket", cx: 340, cy: 320 },
  { name: "Square leg", description: "Square leg-side", cx: 310, cy: 220 },
  { name: "Mid-wicket", description: "In front of square leg", cx: 290, cy: 270 },
  { name: "Mid-on", description: "Straight leg-side", cx: 230, cy: 340 },
  { name: "Long on", description: "Deep straight leg-side", cx: 200, cy: 390 },
  { name: "Bowler", description: "Bowler's end", cx: 200, cy: 130 },
  { name: "Wicket-keeper", description: "Behind batter", cx: 200, cy: 70 },
  { name: "Striker", description: "Batter on strike", cx: 200, cy: 210 },
  { name: "Non-striker", description: "Batter non-striker's end", cx: 200, cy: 130 },
];

export default function CricketGroundView() {
  const [hovered, setHovered] = useState<string | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);

  // Grass stripe colors
  const grass1 = "#4caf50";
  const grass2 = "#388e3c";
  const pitchColor = "#e0b97d";
  const pitchStroke = "#a67c52";
  const symbolColor = "#fffde7";
  const symbolStroke = "#888";
  const symbolRadius = 12;

  // Pitch dimensions (smaller)
  const pitchWidth = 32;
  const pitchHeight = 110;
  const pitchX = 200 - pitchWidth / 2;
  const pitchY = 210 - pitchHeight / 2;

  // Crease and center line positions (relative to pitch)
  const creaseOffset = 18;
  const centerLineY = 210;

  // Wicket (stumps) positions
  const stumpWidth = 2.5;
  const stumpHeight = 14;
  const stumpsGap = 5;
  // Top (bowling) end
  const stumpsTopY = pitchY + creaseOffset - stumpHeight / 2;
  // Bottom (batting) end
  const stumpsBotY = pitchY + pitchHeight - creaseOffset - stumpHeight / 2;
  // X positions for three stumps
  const stumpsX = [200 - stumpsGap, 200, 200 + stumpsGap];

  // Tooltip position adjustment (SVG to screen)
  const handleMouseMove = (e: React.MouseEvent<SVGCircleElement, MouseEvent>) => {
    const svg = e.currentTarget.ownerSVGElement;
    if (svg) {
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const cursorpt = pt.matrixTransform(svg.getScreenCTM()?.inverse());
      setMousePos({ x: cursorpt.x, y: cursorpt.y });
    }
  };

  return (
    <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto relative">
      <svg viewBox="0 0 400 420" className="w-full h-auto">
        {/* Grass stripes pattern */}
        {[...Array(10)].map((_, i) => (
          <ellipse
            key={i}
            cx={200}
            cy={210}
            rx={180 - i * 9}
            ry={200 - i * 10}
            fill={i % 2 === 0 ? grass1 : grass2}
            opacity={0.95}
          />
        ))}
        {/* Colored sectors (pie slices) for each area, alternating green shades, perfectly fit to white lines */}
        {Array.from({ length: 8 }).map((_, i) => {
          const angle1 = (Math.PI * 2 * i) / 8;
          const angle2 = (Math.PI * 2 * (i + 1)) / 8;
          // Striker's wicket origin
          const originX = 200;
          const originY = pitchY + creaseOffset;
          // Inner radius (start gap)
          const r0 = 60;
          // Outer radii (ground edge)
          const rx = 180;
          const ry = 200;
          // Inner arc start/end (ellipse parametric)
          const x0 = originX + r0 * Math.sin(angle1);
          const y0 = originY + r0 * -Math.cos(angle1);
          const x1 = originX + r0 * Math.sin(angle2);
          const y1 = originY + r0 * -Math.cos(angle2);
          // Outer arc start/end (ellipse parametric, centered at 200,210)
          const x2 = 200 + rx * Math.sin(angle2);
          const y2 = 210 + ry * -Math.cos(angle2);
          const x3 = 200 + rx * Math.sin(angle1);
          const y3 = 210 + ry * -Math.cos(angle1);
          // Large arc flag
          const largeArc = 0;
          // Alternate colors
          const fill = i % 2 === 0 ? "#4caf50" : "#81c784";
          return (
            <path
              key={`sector-fill-${i}`}
              d={`M ${x0} ${y0}
                  A ${r0} ${r0} 0 ${largeArc} 1 ${x1} ${y1}
                  L ${x2} ${y2}
                  A ${rx} ${ry} 0 ${largeArc} 0 ${x3} ${y3}
                  Z`}
              fill={fill}
              opacity={0.85}
            />
          );
        })}
        {/* Oval boundary */}
        <ellipse
          cx={200}
          cy={210}
          rx={180}
          ry={200}
          fill="none"
          stroke="#226622"
          strokeWidth={5}
        />
        {/* Pitch (smaller) */}
        <rect
          x={pitchX}
          y={pitchY}
          width={pitchWidth}
          height={pitchHeight}
          fill={pitchColor}
          stroke={pitchStroke}
          strokeWidth={3}
          rx={7}
        />
        {/* Pitch lines: two creases and center line */}
        {/* Bowling crease (top) */}
        <line
          x1={pitchX}
          x2={pitchX + pitchWidth}
          y1={pitchY + creaseOffset}
          y2={pitchY + creaseOffset}
          stroke="#8d6e63"
          strokeWidth={2}
        />
        {/* Popping crease (bottom) */}
        <line
          x1={pitchX}
          x2={pitchX + pitchWidth}
          y1={pitchY + pitchHeight - creaseOffset}
          y2={pitchY + pitchHeight - creaseOffset}
          stroke="#8d6e63"
          strokeWidth={2}
        />
        {/* Center line */}
        <line
          x1={pitchX}
          x2={pitchX + pitchWidth}
          y1={centerLineY}
          y2={centerLineY}
          stroke="#bdb76b"
          strokeDasharray="4,3"
          strokeWidth={1.5}
        />
        {/* Wickets (stumps) - Top (bowling) end */}
        {stumpsX.map((x, i) => (
          <rect
            key={`stump-top-${i}`}
            x={x - stumpWidth / 2}
            y={stumpsTopY}
            width={stumpWidth}
            height={stumpHeight}
            fill="#bdb76b"
            stroke="#8d6e63"
            strokeWidth={0.7}
            rx={1}
          />
        ))}
        {/* Wickets (stumps) - Bottom (batting) end */}
        {stumpsX.map((x, i) => (
          <rect
            key={`stump-bot-${i}`}
            x={x - stumpWidth / 2}
            y={stumpsBotY}
            width={stumpWidth}
            height={stumpHeight}
            fill="#bdb76b"
            stroke="#8d6e63"
            strokeWidth={0.7}
            rx={1}
          />
        ))}
        {/* Striker side arrow and label (now at top, arrow points up) */}
        <g>
          {/* Arrow (upward) */}
          <polygon
            points={`${200 - 7},${pitchY + 7} ${200 + 7},${pitchY + 7} ${200},${pitchY - 14}`}
            fill="#d32f2f"
          />
          {/* Label */}
          <text
            x={200}
            y={pitchY - 20}
            textAnchor="middle"
            fontSize={13}
            fill="#d32f2f"
            fontWeight="bold"
          >
            Striker
          </text>
        </g>
        {/* Bowler side arrow and label (now at bottom, arrow points down) */}
        <g>
          {/* Arrow (downward) */}
          <polygon
            points={`${200 - 7},${pitchY + pitchHeight - 7} ${200 + 7},${pitchY + pitchHeight - 7} ${200},${pitchY + pitchHeight + 14}`}
            fill="#1976d2"
          />
          {/* Label */}
          <text
            x={200}
            y={pitchY + pitchHeight + 28}
            textAnchor="middle"
            fontSize={13}
            fill="#1976d2"
            fontWeight="bold"
          >
            Bowler
          </text>
        </g>
        {/* Symbols for regions */}
        {AREAS.map(area => (
          <circle
            key={area.name}
            cx={area.cx}
            cy={area.cy}
            r={symbolRadius}
            fill={symbolColor}
            stroke={symbolStroke}
            strokeWidth={hovered === area.name ? 3 : 1}
            onMouseEnter={e => {
              setHovered(area.name);
              handleMouseMove(e);
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => {
              setHovered(null);
              setMousePos(null);
            }}
            style={{ cursor: "pointer", opacity: hovered === area.name ? 1 : 0.85 }}
          />
        ))}
        {/* Optionally, add a small symbol or icon inside the circle (e.g., a dot) */}
        {AREAS.map(area => (
          <circle
            key={area.name + "-dot"}
            cx={area.cx}
            cy={area.cy}
            r={3}
            fill="#8d6e63"
            pointerEvents="none"
          />
        ))}
        {/* Dashed 30-yard circle (inner fielding circle) */}
        <ellipse
          cx={200}
          cy={210}
          rx={110}
          ry={122}
          fill="none"
          stroke="#fff"
          strokeWidth={2}
          strokeDasharray="8,6"
        />
        {/* Dashed sector lines from striker's wicket (top batting crease) to ground edge (8 areas) */}
        {Array.from({ length: 8 }).map((_, i) => {
          const angle = (Math.PI * 2 * i) / 8;
          // Striker's crease center (top of pitch)
          const originX = 200;
          const originY = pitchY + creaseOffset;
          // End point at ground edge
          const radius = 200; // matches ground's ry
          const rx = 180; // matches ground's rx
          // Ellipse parametric equations
          const endX = 200 + rx * Math.sin(angle);
          const endY = 210 + radius * -Math.cos(angle);

          // Start the line a bit away from the origin (e.g., 60px along the line)
          const t = 60 / Math.sqrt((endX - originX) ** 2 + (endY - originY) ** 2);
          const startX = originX + t * (endX - originX);
          const startY = originY + t * (endY - originY);

          // Debug number position: 30% from origin to end
          const labelX = originX + 0.3 * (endX - originX);
          const labelY = originY + 0.3 * (endY - originY);

          return (
            <g key={`sector-${i}`}>
              <line
                x1={startX}
                y1={startY}
                x2={endX}
                y2={endY}
                stroke="#fff"
                strokeWidth={1.5}
                strokeDasharray="6,7"
              />
              {/* Debug sector number */}
              <text
                x={labelX}
                y={labelY}
                fontSize={18}
                fill="#ff9800"
                fontWeight="bold"
                textAnchor="middle"
                alignmentBaseline="middle"
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {i + 1}
              </text>
            </g>
          );
        })}
        {/* Off side and On side labels outside the ground */}
        <text
          x={400}
          y={410}
          textAnchor="end"
          fontSize={18}
          fill="#2e7d32"
          fontWeight="bold"
          alignmentBaseline="middle"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          On(Leg) side
        </text>
        <text
          x={10}
          y={410}
          textAnchor="start"
          fontSize={18}
          fill="#1565c0"
          fontWeight="bold"
          alignmentBaseline="middle"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          Off side
        </text>
      </svg>
      {/* Tooltip */}
      {hovered && mousePos && (
        <div
          style={{
            position: "absolute",
            left: `calc(${(mousePos.x / 400) * 100}% + 10px)` ,
            top: `calc(${(mousePos.y / 420) * 100}% - 10px)` ,
            background: "#fffde7",
            color: "#1b5e20",
            border: "1px solid #888",
            borderRadius: 6,
            padding: "6px 12px",
            pointerEvents: "none",
            fontSize: 14,
            fontWeight: 500,
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            zIndex: 10,
            minWidth: 80,
            maxWidth: 180,
            whiteSpace: "normal"
          }}
        >
          <div style={{ fontWeight: 700 }}>{hovered}</div>
          <div style={{ fontSize: 12 }}>{AREAS.find(a => a.name === hovered)?.description}</div>
        </div>
      )}
    </div>
  );
} 